import React, { useState } from 'react';
import CompanyFormModal from '../CompanyFormModal';

// 기업 정보 탭: 등록된 기업 정보를 보여 주고, 수정은 기업 등록 창을 그대로 쓴다.

const show = (value) => (value === undefined || value === null || value === '' ? <span className="empty">–</span> : value);

const payday = (info = {}) => {
  if (info.payday_last_day) return `${info.payday_next_month ? '익월' : '당월'} 말일`;
  if (!info.payday) return '';
  return `${info.payday_next_month ? '익월' : '당월'} ${info.payday}일`;
};

const pad = (n) => String(n).padStart(2, '0');

// 최근 3년의 달별 대장 현황. 칸을 누르면 그 달의 상용직 탭으로 간다.
function LedgerStatus({ months = {}, current, onOpenMonth }) {
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth() + 1;
  const years = [thisYear, thisYear - 1, thisYear - 2];
  const total = years.reduce((n, y) => n + Object.keys(months).filter((k) => k.startsWith(`${y}-`)).length, 0);
  return (
    <section>
      <h4>임금대장 현황 <span className="pr-muted">최근 3년 · 대장 {total}개 · 달을 누르면 그 달 상용직으로 이동합니다</span></h4>
      <table className="pr-status">
        <thead>
          <tr><th />{Array.from({ length: 12 }, (_, i) => <th key={i}>{i + 1}월</th>)}</tr>
        </thead>
        <tbody>
          {years.map((y) => (
            <tr key={y}>
              <th>{y}년</th>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                const info = months[`${y}-${pad(m)}`];
                const isCurrent = current && current.year === y && current.month === m;
                const todo = !info && y === thisYear && m === thisMonth;   // 이번 달인데 아직 대장이 없다
                const detail = info
                  ? `상용직 ${info.fulltime}명${info.daily ? ` · 일용직 ${info.daily}명` : ''}${info.business ? ` · 사업소득 ${info.business}명` : ''}`
                  : '대장 없음 — 누르면 이 달 대장을 만들 수 있습니다';
                return (
                  <td key={m}>
                    <button
                      type="button"
                      className={`pr-status-cell ${info ? 'has' : ''} ${isCurrent ? 'current' : ''} ${todo ? 'todo' : ''}`}
                      title={`${y}년 ${m}월${todo ? ' (이번 달)' : ''} · ${detail}`}
                      onClick={() => onOpenMonth(y, m)}
                    >
                      {info ? (
                        <>
                          <b>{info.fulltime}</b>
                          {(info.daily > 0 || info.business > 0) && <span>+{info.daily + info.business}</span>}
                        </>
                      ) : todo ? '이번 달' : '·'}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pr-muted pr-status-note">숫자는 상용직 인원, +는 일용직·사업소득 인원입니다. 테두리가 굵은 칸이 지금 보고 있는 달, 주황 테두리는 아직 대장이 없는 이번 달입니다.</p>
    </section>
  );
}

function CompanyInfoTab({ company, users = [], months, current, onChanged, onOpenMonth }) {
  const [editing, setEditing] = useState(false);
  const info = company.info || {};
  const manager = company.manager || {};
  const owner = users.find((u) => u.id === company.person_in_charge);

  return (
    <div className="pr-company">
      <div className="pr-company-head">
        <h3>{company.name}</h3>
        <button type="button" className="pr-btn" onClick={() => setEditing(true)}>기업 정보 수정</button>
      </div>

      {/* 화면 폭에 따라 3열·2열·1열로 놓는다. 기타는 내용이 길어 맨 아래에 통으로 둔다 */}
      <div className="pr-company-grid">
      {onOpenMonth && <LedgerStatus months={months} current={current} onOpenMonth={onOpenMonth} />}

      <section>
        <h4>사업자 정보</h4>
        <dl>
          <dt>대표자</dt><dd>{show(info.representative)}</dd>
          <dt>급여지급일</dt><dd>{show(payday(info))}</dd>
          <dt>사업자등록번호</dt><dd>{show(info.business_number)}</dd>
          <dt>사업장관리번호</dt><dd>{show(info.business_registration_code)}</dd>
          <dt>주소</dt><dd className="wide">{show(info.address)}</dd>
        </dl>
      </section>

      <div className="pr-company-stack">
      <section>
        <h4>기본 정보</h4>
        <dl>
          <dt>기업명</dt><dd>{show(company.name)}</dd>
          <dt>기업 별칭</dt><dd>{show(company.alt_name)}</dd>
          <dt>담당자</dt><dd className="wide">{show(owner ? owner.name : company.person_in_charge)}</dd>
        </dl>
      </section>

      <section>
        <h4>기업 측 담당자</h4>
        <dl>
          {[['biz', '사업담당자'], ['fin', '재무담당자']].map(([key, label]) => {
            const person = manager[key] || {};
            return (
              <React.Fragment key={key}>
                <dt>{label}</dt><dd>{show(person.name)}</dd>
                <dt>연락처</dt><dd>{show([person.phone, person.email].filter(Boolean).join(' · '))}</dd>
              </React.Fragment>
            );
          })}
        </dl>
      </section>
      </div>

      <section className="pr-company-full">
        <h4>기타</h4>
        <dl>
          <dt>휴일수당</dt><dd className="wide">{company.contact && company.contact.holiday_pay ? '있음' : '없음'}</dd>
          <dt>추가계약</dt><dd className="wide text">{show(company.contact && company.contact.extra_contract)}</dd>
          <dt>메모</dt><dd className="wide text">{show(company.others && company.others.memo)}</dd>
        </dl>
      </section>
      </div>

      <p className="pr-muted">급여지급일은 계약서·명세서에 찍히는 지급일입니다. 임금대장의 기산 기간은 직원별 지급일 칸으로 정하고, 비우면 1일~말일입니다.</p>

      <CompanyFormModal
        isOpen={editing}
        onClose={() => setEditing(false)}
        onSuccess={(updated) => { setEditing(false); if (onChanged) onChanged(updated); }}
        users={users}
        editingCompany={company}
      />
    </div>
  );
}

export default CompanyInfoTab;
